import { computed, Directive, inject, input, InputSignal, Signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/** A CSS declaration block: `property -> value`. */
type CssDeclarations = Record<string, string>;

/**
 * Shape of the CMS `styles` JSON. Top-level keys are selectors mapping to a declaration block;
 * an `@media …` key maps to a nested map of `selector -> declaration block`.
 */
type CssJson = Record<string, CssDeclarations | Record<string, CssDeclarations>>;

/**
 * Injects a CMS `styles` JSON object onto the host as a scoped inline `<style>` element.
 *
 * Replaces the repeated `<span [innerHTML]="styles | jsonToCss | trustHtml"></span>` pattern: bind
 * the CMS `styles` value and the directive serializes it to CSS and sets the host's `innerHTML`
 * (trusted, since the source is the CMS). Renders nothing when there are no styles.
 *
 * ```html
 * <span [appCmsStyles]="section.styles"></span>
 * ```
 */
@Directive({
    selector: '[appCmsStyles]',
    host: {
        '[innerHTML]': 'styleElement()',
    },
})
export class CmsStyles {
    private readonly _sanitizer: DomSanitizer = inject(DomSanitizer);

    /** The CMS `styles` JSON to serialize and inject. */
    public readonly appCmsStyles: InputSignal<unknown> = input<unknown>();

    protected readonly styleElement: Signal<SafeHtml | null> = computed((): SafeHtml | null => {
        const json: unknown = this.appCmsStyles();

        if (!json || typeof json !== 'object') {
            return null;
        }

        // Defence in depth: the CMS is a trusted source, but a `</style` sequence anywhere in the
        // serialized CSS would close the element early and turn the remainder into live markup.
        // CSS never legitimately contains it (`>` on its own, as a child combinator, is untouched).
        const css: string = this.serialize(json as CssJson).replace(/<\/style/gi, '');

        return this._sanitizer.bypassSecurityTrustHtml(`<style>${css}</style>`);
    });

    private serialize(json: CssJson): string {
        let css: string = '';

        for (const selector of Object.keys(json)) {
            const block: CssDeclarations | Record<string, CssDeclarations> = json[selector];

            if (selector.startsWith('@media')) {
                css += `${selector} {\n`;

                const rules: Record<string, CssDeclarations> = block as Record<
                    string,
                    CssDeclarations
                >;

                for (const nestedSelector of Object.keys(rules)) {
                    css += this.ruleBlock(nestedSelector, rules[nestedSelector], '  ');
                }

                css += '}\n';
            } else {
                css += this.ruleBlock(selector, block as CssDeclarations, '');
            }
        }

        return css;
    }

    private ruleBlock(selector: string, declarations: CssDeclarations, indent: string): string {
        let block: string = `${indent}${selector} {\n`;

        for (const property of Object.keys(declarations)) {
            block += `${indent}  ${property}: ${declarations[property]};\n`;
        }

        return `${block}${indent}}\n`;
    }
}
